import { z } from "zod";
import { ExtraType } from "../../../extras/types";
import { ExtraCategories, ExtraCategory } from "../ExtraCategory.enum";
import { IssueCode, IssueField, IssueTarget, ValidationIssue } from "../entities/ValidationIssue";
import { VehicleType } from "../VehicleType";

const ExtraCategorizationVOSchema = z.object({
  category: z.enum(ExtraCategory).default(ExtraCategory.NONE),
  vehicleType: z.custom<VehicleType>(value => value instanceof VehicleType).optional(),
  requestLicensePlate: z.boolean().default(false),
});

export type ExtraCategorizationVOProps = z.input<typeof ExtraCategorizationVOSchema>;

/**
 * Category of an extra with its vehicle fields. The vehicle type and the
 * license plate flag only exist for a Vehicle extra: any other category drops them.
 */
export class ExtraCategorizationVO {
  public readonly category: ExtraCategory;
  public readonly vehicleType?: VehicleType;
  public readonly requestLicensePlate: boolean;

  private constructor(props: z.infer<typeof ExtraCategorizationVOSchema>) {
    this.category = props.category;
    const isVehicle = props.category === ExtraCategory.VEHICLE;
    this.vehicleType = isVehicle ? props.vehicleType : undefined;
    this.requestLicensePlate = isVehicle && props.requestLicensePlate;
  }

  static of(props: ExtraCategorizationVOProps = {}): ExtraCategorizationVO {
    return new ExtraCategorizationVO(ExtraCategorizationVOSchema.parse(props));
  }

  /** *No category*: the value of a new extra and of an absent `categorization`. */
  static none(): ExtraCategorizationVO {
    return ExtraCategorizationVO.of();
  }

  isVehicle(): boolean {
    return this.category === ExtraCategory.VEHICLE;
  }

  /** Changing the category away from Vehicle clears the vehicle fields. */
  withCategory(category: ExtraCategory): ExtraCategorizationVO {
    return ExtraCategorizationVO.of({
      category,
      vehicleType: this.vehicleType,
      requestLicensePlate: this.requestLicensePlate,
    });
  }

  /**
   * The categorization kept for a new extra type: reset for an unsupported type, kept
   * between the two supported types.
   */
  forExtraType(type: ExtraType | string | undefined): ExtraCategorizationVO {
    return ExtraCategories.supportsExtraType(type) ? this : ExtraCategorizationVO.none();
  }

  /** Client pre-check: a Vehicle extra needs a vehicle type. Same issue as the server. */
  preCheck(): ValidationIssue[] {
    if (this.isVehicle() && !this.vehicleType) {
      return [
        ValidationIssue.of({
          code: IssueCode.EXTRA_VEHICLE_TYPE_REQUIRED,
          target: IssueTarget.of({ field: IssueField.VEHICLE_TYPE }),
        }),
      ];
    }
    return [];
  }

  equals(other: ExtraCategorizationVO): boolean {
    return (
      this.category === other.category &&
      this.requestLicensePlate === other.requestLicensePlate &&
      (this.vehicleType?.code ?? undefined) === (other.vehicleType?.code ?? undefined)
    );
  }
}
